import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { BaseUserProfile, IAccountChangeValidationRequired } from 'fitbeat.models';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { translate } from 'react-i18next';
import { Platform, SafeAreaView, StatusBar, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import FitillionLogo from '../assets/images/fitllion-horizontal-logo-gym-smart.svg';
import headerImage from '../assets/images/header-images/studio-header.jpg';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
import { Button, ButtonTypes } from '../components';
import { IAppStore } from '../HomeWorkouts';
import { UserProfileService } from '../services';
import Theme from '../theme';
import { TrackingImageTile } from '../Tracking/components/TrackingImageTile';
import {
  clearDeepLinkParams as clearDeepLinkParamsAction,
  defaultScale,
  scaleImageAspectRatio,
  UserDataConsumer,
} from '../utils';
import { isDesktop, isMobileWeb } from '../utils/ScreenSizes';

interface IProps {
  user: {
    userProfile: UserProfile;
    userProfileService: UserProfileService;
    userServiceClient: UserServiceClient;
  };
  hasAccessToAStudioGym: boolean;
  navigation: {
    navigate: (route: string) => void;
    getParam: (key: string) => unknown;
  };
  clearDeepLinkParams: (keys: string[]) => void;
  planId?: string;
  t: (key: string, options?: any) => string;
}

const StripeAccountChangeConfirmation: FunctionComponent<IProps> = (
  {
    planId,
    navigation,
    user,
    clearDeepLinkParams,
    t,
    hasAccessToAStudioGym,
  },
) => {

  const [accountChangeValidation, setAccountChangeValidation] = useState<null | IAccountChangeValidationRequired>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<null | string>(null);

  const isAccountChangeValidation = (obj: any): obj is IAccountChangeValidationRequired => {
    return typeof obj.accountChangeNeeded === 'boolean' &&
      typeof obj.currentAccountStatus === 'string' &&
      typeof obj.changeTo?.displayName === 'string';
  };

  const moveUserAccount = async () => {
    try {
      setLoading(true);
      if (!(accountChangeValidation && planId)) {
        throw new Error('account change validation and planId required to change account');
      }
      const { userProfileService, userServiceClient } = user;
      const { gymName } = accountChangeValidation.changeTo;
      await userServiceClient.changeUserStripeAccountToAccountWithPlanID(planId, gymName);
      await userProfileService.refreshCurrentUserProfile();
      navigation.navigate('PreLoadedPlanDetails');
    } catch (e) {
      setError('Request failed. Please contact support');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const changeValidation = navigation.getParam('accountChangeValidation');
    if (isAccountChangeValidation(changeValidation)) {
      setAccountChangeValidation(changeValidation);
    } else {
      navigateToDashboard();
    }
    if (Platform.OS === 'ios') {
      StatusBar.setBarStyle('light-content');
    }
    return () => {
      if (Platform.OS === 'ios') {
        StatusBar.setBarStyle('dark-content');
      }
    };
  }, []);

  const navigateToDashboard = () => {
    clearDeepLinkParams([
      'pendingPlanId',
      'pendingCoupon',
      'route',
      'planId',
      'coupon',
      'gymName',
    ]);
    navigation.navigate('SummaryDashboard');
  };

  const hasActivePlan = BaseUserProfile.hasActivePlan(user.userProfile);
  const isStudioUserWithActivePlan = hasAccessToAStudioGym && hasActivePlan;

  return (
    <View style={styles.container}>
      {!isDesktop() &&
        <TrackingImageTile
          onClosePress={navigateToDashboard}
          imageSource={headerImage}
          containerStyle={styles.headerImageContainer}
          actions={<></>}
        />
      }
      {
        accountChangeValidation &&
        <View style={styles.contentContainer}>
          <View style={{ flex: 1, justifyContent: 'space-evenly' }}>
            {!isDesktop() &&
              <FitillionLogo height={defaultScale(55)} fill={Theme.BLACK} />
            }
            <View style={{ alignItems: 'center' }}>
              {isStudioUserWithActivePlan &&
                <Text style={styles.heading}>
                  {t('pleaseCancelSubscriptionBeforeJoiningNewStudio')}
                </Text>
              }
              {!isStudioUserWithActivePlan &&
                <>
                  <Text style={styles.heading}>
                    {t(hasActivePlan ? 'doYouWantToJoinStudio' : 'welcomeToStudio',
                      { gymName: accountChangeValidation.changeTo?.displayName })}
                  </Text>
                  {hasActivePlan &&
                    <Text style={styles.normalText}>
                      {t('thisWillReplaceCurrentSubscription')}
                    </Text>
                  }
                </>
              }
            </View>
          </View>
          <SafeAreaView>
            {error &&
              <Text style={styles.error}>{error}</Text>
            }
            {!isStudioUserWithActivePlan &&
              <Button
                title={
                  hasActivePlan
                    ? t('yesJoinStudio', { gymName: accountChangeValidation.changeTo.displayName })
                    : t('continueToPayment')
                }
                fontColor={Theme.WHITE}
                isLoading={loading}
                onPress={moveUserAccount}
              />
            }
            {hasActivePlan &&
              <Button
                buttonType={ButtonTypes.OUTLINE}
                title={t(isStudioUserWithActivePlan ? 'backToDashboard' : 'noKeepExistingMembership')}
                onPress={navigateToDashboard}
                disabled={loading}
                containerStyle={{ marginTop: defaultScale(10) }}
              />
            }
          </SafeAreaView>
        </View>
      }
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.WHITE,
    flex: 1,
    height: isDesktop() ? '50%' : '100%',
    width: '100%',
  },
  headerImageContainer: {
    ...Platform.select({
      web: {
        height: '50%',
      },
      default: {
        height: undefined,
      },
    }),
    width: '100%',
    aspectRatio: scaleImageAspectRatio(1),
  },
  contentContainer: {
    padding: defaultScale(15),
    paddingTop: 0,
    flex: 1,
  },
  heading: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    marginBottom: defaultScale(5),
  },
  normalText: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
  error: {
    color: Theme.RED,
  },
});

const mapStateToProps = (state: IAppStore): Partial<IProps> => {
  return {
    planId: state.deepLinkParamaters.planId,
    hasAccessToAStudioGym: hasAccessToAStudioGymSelector(state),
  };
};

const mapDispatchToProps = (dispatch): Partial<IProps> => {
  return {
    clearDeepLinkParams: (keysToClear) => dispatch(clearDeepLinkParamsAction(keysToClear)),
  };
};

export default UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(translate(
  ['payments'], { wait: true })(StripeAccountChangeConfirmation)));
